<template>
  <div>
    <breadcrumb-list>
      <breadcrumb-link view="Home" label="Home" />
      <breadcrumb-link view="BlanketVOBList" label="Blanket VOB Search" />
      <breadcrumb-link view="BlanketVOBCreate" label="New" :active="true" />
    </breadcrumb-list>
    <section>
      <div class="container is-max-hd">
        <div class="columns is-flex-wrap-wrap">
          <div class="column is-two-thirds-desktop form-column">
            <div class="box" style="position: relative">
              <b-loading :active="loadingPatient" :is-full-page="false" />
              <div class="header-container">
                <h1 class="title">New Blanket VOB</h1>
              </div>

              <!-- Floating warning banner -->
              <FloatingWarningBanner
                :show="showWarningBanner"
                title="We're still processing your request"
                message="It may take longer to process. Feel free to leave this page and check Blanket VOB List shortly."
                @close="closeWarningBanner"
              />

              <hr />
              <form @submit.prevent="handleSubmit">
                <div class="block">
                  <div>
                    <AjaxSelectField
                      label="Select a provider"
                      placeholder="e.g. Healthcare Facility"
                      :options="providers"
                      :errors="errors.providerId"
                      v-model="form.providerId"
                      :loading="loadingProviders"
                    />
                    <AjaxSelectField
                      label="Select the Patient's state of residence"
                      placeholder="e.g. California"
                      :options="states"
                      :errors="errors.patientStateId"
                      v-model="form.patientStateId"
                      :loading="loadingStates"
                    />
                  </div>
                </div>

                <hr />

                <div class="columns">
                  <div class="column">
                    <b-field
                      label="Patient First Name"
                      :message="errors.patientFirstName"
                      :type="{ 'is-danger': errors.patientFirstName }"
                    >
                      <b-input
                        v-model="form.patientFirstName"
                        placeholder="Enter first name"
                        required
                      />
                    </b-field>
                  </div>
                  <div class="column">
                    <b-field
                      label="Patient Last Name"
                      :message="errors.patientLastName"
                      :type="{ 'is-danger': errors.patientLastName }"
                    >
                      <b-input
                        v-model="form.patientLastName"
                        placeholder="Enter last name"
                        required
                      />
                    </b-field>
                  </div>
                </div>

                <div class="columns">
                  <div class="column">
                    <b-field
                      label="Patient Date of Birth"
                      :message="errors.patientDOB"
                      :type="{ 'is-danger': errors.patientDOB }"
                    >
                      <b-datepicker
                        v-model="form.patientDOB"
                        :min-date="minDate"
                        :max-date="maxDate"
                        :date-parser="dateParser"
                        editable
                        locale="en-US"
                        placeholder="Click to select..."
                        icon="calendar-today"
                        :mobile-modal="false"
                        :mobile-native="false"
                        trap-focus
                        :use-html5-validation="false"
                        required
                      />
                    </b-field>
                  </div>
                  <div class="column">
                    <b-field
                      label="Patient SSN (Optional)"
                      :message="errors.patientSSN || 'Format: XXX-XX-XXXX or XXXXXXXXX'"
                      :type="{ 'is-danger': errors.patientSSN }"
                    >
                      <b-input ref="ssnInput" :value="formattedSSN" @input="handleSSNInput" placeholder="Enter SSN" type="text"
                        inputmode="numeric" />
                    </b-field>
                  </div>
                </div>

                <div class="submit-button-container">
                  <b-button
                    type="is-primary"
                    :disabled="submitting || loadingPatient"
                    native-type="submit"
                    size="is-medium"
                    class="submit-button"
                  >
                    <span v-if="!submitting">Create Blanket VOB Search</span>
                    <span v-else class="loading-content">
                      <b-icon icon="loading" class="is-spinning loading-icon"></b-icon>
                      <span>Creating Blanket VOB Search...</span>
                    </span>
                  </b-button>
                </div>
              </form>
            </div>
          </div>
          <div class="column instructions-column">
            <div class="box instructions-box">
              <div class="instructions-header" @click="toggleInstructions">
                <h4 class="title is-5 instructions-title">
                  <b-icon icon="information-outline" size="is-small" class="mr-2"></b-icon>
                  Instructions
                </h4>
                <b-icon
                  :icon="isInstructionsExpanded ? 'chevron-up' : 'chevron-down'"
                  class="toggle-icon"
                ></b-icon>
              </div>
              <div
                class="instructions-content"
                :class="{ 'is-collapsed': !isInstructionsExpanded }"
              >
                <hr />
                <div class="content">
                  <p>
                    Please fill in all the required information to create a new blanket VOB search:
                  </p>
                  <ul>
                    <li>Select a provider from the dropdown list</li>
                    <li>Select the patient's state</li>
                    <li>Enter the patient's first and last name</li>
                    <li>Select the patient's date of birth</li>
                    <li>Optionally enter the patient's SSN (Social Security Number) XXX-XX-XXXX</li>
                  </ul>
                  <b-notification type="is-info is-light" :closable="false" class="mt-4">
                    Each Blanket Search counts as a transaction
                  </b-notification>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { dateParser } from '../utils/dateParser';
import request from '../request';
import { format } from 'date-fns';
import AjaxSelectField from '../components/fields/AjaxSelectField.vue';
import FloatingWarningBanner from '../components/FloatingWarningBanner.vue';

export default {
  name: 'BlanketVOBCreate',
  components: {
    AjaxSelectField,
    FloatingWarningBanner,
  },
  data() {
    return {
      form: {
        providerId: null,
        patientStateId: null,
        patientFirstName: '',
        patientLastName: '',
        patientDOB: null,
        patientSSN: '',
      },
      providers: [],
      states: [],
      loadingProviders: true,
      loadingStates: true,
      loadingPatient: Boolean(this.$route.query.verification_id),
      submitting: false,
      errors: {},
      showWarningBanner: false,
      warningTimer: null,
      isInstructionsExpanded: window.innerWidth > 1024,
      isMobile: window.innerWidth <= 1024,
    };
  },
  computed: {
    // Min date: 100 years ago
    minDate() {
      const date = new Date();
      date.setFullYear(date.getFullYear() - 100);
      return date;
    },
    // Max date: current date
    maxDate() {
      return new Date();
    },
    formattedSSN() {
      const digits = this.form.patientSSN || '';

      if (digits.length <= 3) return digits;
      if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5, 9)}`;
    }
  },
  async created() {
    this.fetchProviders();
    await this.fetchStates();
    await this.loadPatientVerification();
  },
  mounted() {
    window.addEventListener('resize', this.handleResize);
    this.handleResize();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    async loadPatientVerification() {
      const { verification_id: verificationID, patient_index: patientIndex } = this.$route.query;
      if (!verificationID) return;
      try {
        if (
          typeof verificationID !== 'string' ||
          typeof patientIndex !== 'string' ||
          !/^\d+$/.test(patientIndex)
        ) {
          throw new Error('Invalid patient selection');
        }
        const response = await request.get(
          `/patient-verifications/${encodeURIComponent(verificationID)}`
        );
        const verification = response.data.patient_verification;
        const patients = verification.response?.Patients || verification.response?.patients;
        const patient = Array.isArray(patients)
          ? patients.filter(
              (patient) => patient && typeof patient === 'object' && !Array.isArray(patient)
            )[Number(patientIndex)]
          : null;
        if (verification.status !== 'complete' || !patient) {
          throw new Error('Patient result unavailable');
        }
        this.form.patientFirstName = patient.FirstName || '';
        this.form.patientLastName = patient.LastName || '';
        this.form.patientDOB = dateParser(patient.DOB || '');
        const ssn = String(patient.SSN || '').replace(/[-\s]/g, '');
        this.form.patientSSN = /^\d{9}$/.test(ssn) ? ssn : '';
        const state = this.states.find(
          (state) =>
            state.abbreviation === String(patient.Address?.State || '').trim().toUpperCase()
        );
        this.form.patientStateId = state ? state.id : null;
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Unable to load the selected patient. Please return to the verification and try again.',
          type: 'is-danger',
        });
      } finally {
        this.loadingPatient = false;
      }
    },
    toggleInstructions() {
      if (this.isMobile) {
        this.isInstructionsExpanded = !this.isInstructionsExpanded;
      }
    },
    handleResize() {
      this.isMobile = window.innerWidth <= 1024;
      if (!this.isMobile) {
        this.isInstructionsExpanded = true;
      }
    },
    dateParser: dateParser,
    async fetchProviders() {
      try {
        const response = await request('/providers/compact');
        this.providers = response.data.providers;
        this.providers = this.providers.filter((provider) => provider.is_active);
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load providers',
          type: 'is-danger',
        });
      } finally {
        this.loadingProviders = false;
      }
    },
    async fetchStates() {
      try {
        const response = await request('/blanket-vob-search/patient-states');
        this.states = response.data.map((state) => ({
          id: state.id,
          name: state.name,
          abbreviation: state.abbreviation,
        }));
      } catch (error) {
        this.$buefy.toast.open({
          message: 'Failed to load states',
          type: 'is-danger',
        });
      } finally {
        this.loadingStates = false;
      }
    },
    dateFormatter(date) {
      return format(date, 'MM/dd/yyyy');
    },
    validateForm() {
      const errors = {};
      const ssn = (this.form.patientSSN || '').replace(/\D/g, '');
      if (ssn.length > 9) {
        errors.patientSSN = 'SSN cannot exceed 9 digits';
      }
      if (ssn && ssn.length !== 9) {
        errors.patientSSN = 'SSN must be exactly 9 digits';
      }
      // Extend with more validations as needed
      return errors;
    },
    async handleSubmit() {
      this.submitting = true;
      this.errors = {};
      this.showWarningBanner = false;

      // Use the new validateForm method
      const validationErrors = this.validateForm();
      if (Object.keys(validationErrors).length > 0) {
        this.errors = validationErrors;
        this.submitting = false;
        return;
      }

      this.warningTimer = setTimeout(() => {
        this.showWarningBanner = true;
      }, 20000);

      try {
        await request('/blanket-vob-search', {
          method: 'POST',
          data: {
            ...this.form,
            patientDOB: this.form.patientDOB ? format(this.form.patientDOB, 'MM/dd/yyyy') : null,
          },
        });

        this.$buefy.toast.open({
          message: 'Blanket VOB search created successfully',
          type: 'is-success',
        });

        // Redirect to the insurance discovery list
        this.$router.push({
          name: 'BlanketVOBList',
        });
      } catch (error) {
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$buefy.toast.open({
            message: error.response?.data?.error || 'Failed to create blanket VOB search',
            type: 'is-danger',
          });
        }
      } finally {
        this.submitting = false;
        this.showWarningBanner = false;
        if (this.warningTimer) {
          clearTimeout(this.warningTimer);
          this.warningTimer = null;
        }
      }
    },
    closeWarningBanner() {
      this.showWarningBanner = false;
    },
    handleSSNInput(value) {
      const rawValue = value || '';

      const hasInvalidChars = /[^0-9-\s]/.test(rawValue);

      const digitsOnly = rawValue.replace(/\D/g, '');
      const limitedDigits = digitsOnly.slice(0, 9);

      this.form.patientSSN = limitedDigits;

      // Show error ONLY if user typed invalid characters
      if (hasInvalidChars) {
        this.$set(this.errors, 'patientSSN', 'SSN must contain digits only');
      } else {
        // Clear error if input becomes valid
        if (this.errors.patientSSN) {
          this.$delete(this.errors, 'patientSSN');
        }
      }

      this.$nextTick(() => {
        if (this.$refs.ssnInput?.$el) {
          const inputEl = this.$refs.ssnInput.$el.querySelector('input');
          if (inputEl) {
            inputEl.value = this.formattedSSN;
          }
        }
      });
    },
  },
};
</script>

<style scoped>
.box {
  padding: 2rem;
}

.block {
  margin-bottom: 1.5rem;
}

.submit-button-container {
  margin-top: 1.5rem;
  width: 100%;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.submit-button .loading-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: fadeIn 0.3s ease-in;
}

.submit-button .loading-icon {
  font-size: 1rem;
  color: white;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Spinning animation for loading icon */
.is-spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Header container for title */
.header-container {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.header-container .title {
  margin-bottom: 0;
  flex-shrink: 0;
}

/* Override Buefy's built-in loading styles */
.submit-button.is-loading {
  pointer-events: none;
}

.submit-button.is-loading::after {
  display: none !important;
}

/* Instructions card styles */
.instructions-box {
  background-color: #fff;
  border: 1px solid #e9ecef;
}

.instructions-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.instructions-title {
  display: flex;
  align-items: center;
  color: #495057;
  margin-bottom: 0 !important;
}

.toggle-icon {
  display: none;
  color: #6c757d;
  transition: transform 0.2s ease;
}

.instructions-content {
  overflow: hidden;
  transition:
    max-height 0.3s ease,
    opacity 0.3s ease;
  max-height: 1000px;
  opacity: 1;
}

.instructions-content hr {
  background-color: #dee2e6;
  height: 1px;
  margin: 1rem 0;
}

@media (max-width: 1024px) {
  .columns.is-flex-wrap-wrap {
    display: flex;
    flex-direction: column;
  }

  .instructions-column {
    order: -1;
    margin-top: 1rem;
    margin-bottom: 0;
  }

  .form-column {
    order: 1;
  }

  .instructions-header {
    cursor: pointer;
  }

  .toggle-icon {
    display: block;
  }

  .instructions-content.is-collapsed {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
  }

  .submit-button-container {
    width: 100%;
  }

  .submit-button {
    width: 100%;
  }
}
</style>
